/**
 * Helper functions to mix journey workflow node data with channel report data
 * based on node_uid and node type/subtype.
 */

/**
 * Finds matching report item from journeyReport for a given workflow node based on node_uid and type.
 * @param {Object} node - Workflow node object containing node_uid/id and type/subtype
 * @param {Object|Array} journeyReport - Report data object or array from channel report API
 * @returns {Object|null} Matching report object or null
 */
export const findMatchingReportNode = (node, journeyReport) => {
  if (!node || !journeyReport) {
    return null
  }

  const nodeUid = String(node.node_uid || node.id || node.data?.node_uid || '').trim()
  const nodeType = String(node.type || node.subtype || node.item_type || node.category || node.data?.type || '')
    .toLowerCase()
    .trim()

  if (!nodeUid && !nodeType) {
    return null
  }

  // Extract list of report items
  let reportItems = []
  if (Array.isArray(journeyReport)) {
    reportItems = journeyReport
  } else if (Array.isArray(journeyReport.nodes)) {
    reportItems = journeyReport.nodes
  } else if (Array.isArray(journeyReport.node_reports)) {
    reportItems = journeyReport.node_reports
  } else if (Array.isArray(journeyReport.reports)) {
    reportItems = journeyReport.reports
  } else if (Array.isArray(journeyReport.data)) {
    reportItems = journeyReport.data
  } else if (typeof journeyReport === 'object') {
    // If keyed directly by node_uid
    if (nodeUid && (journeyReport[nodeUid] || journeyReport.nodes?.[nodeUid])) {
      const match = journeyReport[nodeUid] || journeyReport.nodes[nodeUid]
      if (match && typeof match === 'object') {
        const rType = String(match.type || match.subtype || match.channel || match.item_type || '')
          .toLowerCase()
          .trim()
        if (!rType || !nodeType || rType === nodeType || rType.includes(nodeType) || nodeType.includes(rType)) {
          return match
        }
      }
    }
    // Turn dictionary into values array
    reportItems = Object.values(journeyReport.nodes || journeyReport)
  }

  if (!Array.isArray(reportItems) || reportItems.length === 0) {
    return null
  }

  // Priority 1: Match BOTH node_uid and type
  let match = reportItems.find(r => {
    if (!r || typeof r !== 'object') {
      return false
    }
    const rUid = String(r.node_uid || r.id || r.data?.node_uid || '').trim()
    const rType = String(r.type || r.subtype || r.channel || r.item_type || r.data?.type || '')
      .toLowerCase()
      .trim()

    const uidMatch = nodeUid && rUid && nodeUid === rUid
    const isConditionGroup = t => ['condition', 'case', 'switch', 'if-else'].some(c => t.includes(c))
    const typeMatch =
      !nodeType ||
      !rType ||
      nodeType === rType ||
      nodeType.includes(rType) ||
      rType.includes(nodeType) ||
      (isConditionGroup(nodeType) && isConditionGroup(rType))

    return uidMatch && typeMatch
  })

  // Priority 2: Fallback to match by node_uid only
  if (!match && nodeUid) {
    match = reportItems.find(r => {
      if (!r || typeof r !== 'object') {
        return false
      }
      const rUid = String(r.node_uid || r.id || r.data?.node_uid || '').trim()
      return rUid && nodeUid === rUid
    })
  }

  return match || null
}

/**
 * Mixes journey workflow nodes with channel & condition report metrics based on node_uid and type.
 * Attaches channel_report cleanly at node root (node.channel_report) without duplication in node.data
 * or self-nesting.
 * @param {Array|Object} nodes - Journey workflow nodes (array or object map)
 * @param {Object|Array} journeyReport - Report metrics from channel report API
 * @returns {Array|Object} Mixed workflow nodes with channel_report metrics attached
 */
export const mixJourneyDataWithReport = (nodes, journeyReport) => {
  if (!nodes || !journeyReport) {
    return nodes
  }

  const isArray = Array.isArray(nodes)
  const nodeList = isArray ? nodes : Object.values(nodes)

  const updatedList = nodeList.map(node => {
    if (!node || typeof node !== 'object') {
      return node
    }

    const reportMatch = findMatchingReportNode(node, journeyReport)
    if (!reportMatch) {
      return node
    }

    // Extract nested channel_report object if present, flattening structure
    const innerReport =
      reportMatch.channel_report && typeof reportMatch.channel_report === 'object' ? reportMatch.channel_report : {}
    const { channel_report: _nested, ...flatMatch } = reportMatch

    const existingReport = node.channel_report || node.report || {}
    const { channel_report: _existingNested, ...flatExisting } = existingReport

    const mergedReport = {
      ...flatExisting,
      ...innerReport,
      ...flatMatch,
      traversal: flatMatch.traversal || innerReport.traversal || flatExisting.traversal,
      branches: flatMatch.branches || innerReport.branches || flatExisting.branches,
      checked: flatMatch.checked ?? innerReport.checked ?? flatExisting.checked ?? 0,
      in_wait: flatMatch.in_wait ?? flatMatch.in_wait_node ?? innerReport.in_wait ?? flatExisting.in_wait ?? 0,
      emailed: flatMatch.emailed ?? innerReport.emailed ?? flatExisting.emailed ?? 0,
      bypass: flatMatch.bypass ?? innerReport.bypass ?? flatExisting.bypass ?? 0,
      skip: flatMatch.skip ?? innerReport.skip ?? flatExisting.skip ?? 0,
      jump: flatMatch.jump ?? innerReport.jump ?? flatExisting.jump ?? 0,
      exit: flatMatch.exit ?? innerReport.exit ?? flatExisting.exit ?? 0,
      count: flatMatch.count ?? innerReport.count ?? flatExisting.count ?? 0,
      sent: flatMatch.sent ?? innerReport.sent ?? flatExisting.sent ?? 0,
      delivered: flatMatch.delivered ?? innerReport.delivered ?? flatExisting.delivered ?? 0,
      opened: flatMatch.opened ?? innerReport.opened ?? flatExisting.opened ?? 0,
      clicks:
        flatMatch.clicks ?? flatMatch.clicked ?? innerReport.clicks ?? innerReport.clicked ?? flatExisting.clicks ?? 0,
      bounces: flatMatch.bounces ?? innerReport.bounces ?? flatExisting.bounces ?? 0,
      delivery_rate: flatMatch.delivery_rate ?? innerReport.delivery_rate ?? flatExisting.delivery_rate,
      open_rate: flatMatch.open_rate ?? innerReport.open_rate ?? flatExisting.open_rate,
      click_rate: flatMatch.click_rate ?? innerReport.click_rate ?? flatExisting.click_rate,
      bounce_rate: flatMatch.bounce_rate ?? innerReport.bounce_rate ?? flatExisting.bounce_rate
    }

    // Clean up node.data to prevent duplicate channel_report nesting in data
    const updatedData = { ...(node.data || {}) }
    if ('channel_report' in updatedData) {
      delete updatedData.channel_report
    }

    return {
      ...node,
      channel_report: mergedReport,
      data: updatedData
    }
  })

  if (isArray) {
    return updatedList
  }

  const updatedMap = {}
  updatedList.forEach(node => {
    const key = node.id || node.node_uid
    if (key) {
      updatedMap[key] = node
    }
  })
  return updatedMap
}
